import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { leafHead } from "@/lib/seo/leaf-head";
import { SubHubGrid, ContentSection, FaqBlock, buildFaqLd, buildBreadcrumbLd, ResultCard, ReservedResultsArea } from "@/components/age-calculator/shared";
import { TripleDateInput, AnalyzeButton, AnalyzingOverlay, WhatsAppShareButton, useAnalyzeGate, todayISO } from "@/components/age-calculator/premium";

const TITLE = "Chronological Age Calculator - Find Date Difference Online";
const DESC = "Calculate the exact chronological age and structural time difference between any two custom dates for legal forms, job exams, or records.";

const FAQS = [
  { q: "What is chronological age?", a: "Chronological age is the exact elapsed time between two dates expressed in years, months and days — the standard measurement used on birth certificates, school admissions and job exam eligibility." },
  { q: "How is it different from a normal age calculator?", a: "A normal age calculator assumes 'today' as the endpoint. A chronological / date-difference calculator lets you pick both dates so you can compute age 'as on' any cut-off date — exactly what exam boards require." },
  { q: "How are total weeks and intervening months calculated?", a: "Total weeks = floor(total days / 7). Intervening months counts each calendar-month boundary crossed between the two dates, which is different from the residual month count after years." },
  { q: "Does it handle leap years correctly?", a: "Yes. All computation runs on native JavaScript Date arithmetic which respects Gregorian leap-year rules including the 100/400 exception." },
];

export const Route = createFileRoute("/age-calculator/chronological-date-difference")({
  head: () => leafHead({ path: "/age-calculator/chronological-date-difference", title: TITLE, description: DESC, scripts: [buildFaqLd(FAQS), buildBreadcrumbLd([{ name: "Home", url: "https://drhint.com/" }, { name: "Age Calculator", url: "https://drhint.com/age-calculator" }, { name: "Chronological & Date Difference", url: "https://drhint.com/age-calculator/chronological-date-difference" }])] }),
  component: Page,
});

function Page() {
  const today = todayISO();
  const [start, setStart] = useState("");
  const [end, setEnd] = useState(today);
  const [includeEnd, setIncludeEnd] = useState(false);
  const { analyzing, revealed, trigger, reset } = useAnalyzeGate();
  const canGo = !!start && !!end;

  const r = useMemo(() => {
    if (!start || !end) return null;
    const a = new Date(start);
    const b = new Date(end);
    if (isNaN(a.getTime()) || isNaN(b.getTime())) return null;
    const [from, to] = a <= b ? [a, b] : [b, a];
    let years = to.getFullYear() - from.getFullYear();
    let months = to.getMonth() - from.getMonth();
    let days = to.getDate() - from.getDate();
    if (days < 0) { months -= 1; days += new Date(to.getFullYear(), to.getMonth(), 0).getDate(); }
    if (months < 0) { years -= 1; months += 12; }
    const rawDays = Math.floor((to.getTime() - from.getTime()) / 86400000);
    const totalDays = includeEnd ? rawDays + 1 : rawDays;
    const totalWeeks = Math.floor(totalDays / 7);
    const remDays = totalDays % 7;
    const interveningMonths = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
    return { years, months, days, totalDays, totalWeeks, remDays, interveningMonths };
  }, [start, end, includeEnd]);

  const shareMsg = r ? `📅 Date Difference Result\n\n⏳ ${r.years}y ${r.months}m ${r.days}d (${r.totalDays.toLocaleString()} days)\n📆 ${r.totalWeeks} weeks · ${r.interveningMonths} months\n\nCalculated on https://drhint.com/age-calculator/chronological-date-difference` : "";

  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 pt-10 pb-4">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/age-calculator" className="hover:text-primary">Age Calculator</Link> / Chronological & Date Difference
        </nav>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">{TITLE}</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">{DESC}</p>
      </section>
      <section className="mx-auto max-w-5xl px-4">
        <div className="rounded-3xl border border-border bg-card p-6 shadow-sm">
          <div className="grid gap-5 sm:grid-cols-2">
            <TripleDateInput label="Start Date" value={start} onChange={(v) => { setStart(v); reset(); }} />
            <TripleDateInput label="End Date" value={end} onChange={(v) => { setEnd(v); reset(); }} quickChips />
          </div>
          <label className="mt-4 inline-flex items-center gap-2 text-sm cursor-pointer select-none">
            <input type="checkbox" checked={includeEnd} onChange={(e) => { setIncludeEnd(e.target.checked); reset(); }} className="accent-primary" />
            <span className="font-medium">Include end day in calculation</span>
          </label>
          <AnalyzeButton onClick={trigger} disabled={!canGo || analyzing} label="Calculate Difference" />
          <AnalyzingOverlay visible={analyzing} />
          <ReservedResultsArea minHeight={240}>
            {revealed && r ? (
              <>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <ResultCard label="Chronological Age" value={`${r.years}y ${r.months}m ${r.days}d`} />
                  <ResultCard label="Total Days" value={r.totalDays.toLocaleString()} sub={includeEnd ? "Inclusive of end day" : "Exclusive"} />
                  <ResultCard label="Total Weeks" value={r.totalWeeks.toLocaleString()} sub={`+${r.remDays} extra days`} />
                  <ResultCard label="Intervening Months" value={r.interveningMonths.toLocaleString()} />
                </div>
                <div className="mt-6 overflow-x-auto rounded-2xl border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted"><tr><th className="p-3 text-left font-semibold">Metric</th><th className="p-3 text-left font-semibold">Weeks basis</th><th className="p-3 text-left font-semibold">Days basis</th></tr></thead>
                    <tbody>
                      <tr className="border-t border-border"><td className="p-3 font-medium">Net elapsed</td><td className="p-3 tabular-nums">{r.totalWeeks} weeks</td><td className="p-3 tabular-nums">{r.totalDays.toLocaleString()} days</td></tr>
                      <tr className="border-t border-border"><td className="p-3 font-medium">Structured</td><td className="p-3 tabular-nums">{r.interveningMonths} months</td><td className="p-3 tabular-nums">{`${r.years}y ${r.months}m ${r.days}d`}</td></tr>
                    </tbody>
                  </table>
                </div>
                <WhatsAppShareButton message={shareMsg} label="Share Result on WhatsApp" />
              </>
            ) : !analyzing ? (
              <div className="mt-6 rounded-2xl border border-dashed border-border/70 p-8 text-center text-sm text-muted-foreground">Select both dates and tap Calculate.</div>
            ) : null}
          </ReservedResultsArea>
        </div>
      </section>
      <ContentSection title="Chronological Age vs. Simple Date Subtraction">
        <p>
          Government exam boards, immigration officers and legal forms almost always ask for age <em>"as on"</em> a
          particular cut-off date — not today. This tool exists for exactly that. Enter the person's date of birth as the
          start date and the eligibility cut-off as the end date, and you get a chronological breakdown that matches the
          format printed on official forms: <strong>years, months, days</strong>.
        </p>
        <p>
          Simple day-subtraction ("total days ÷ 365.25") drifts by up to two weeks for adults — enough to fail an
          eligibility check. The calendar-based method used here never drifts because it borrows real month lengths and
          respects Gregorian leap-year rules.
        </p>
        <h3>When to use each output</h3>
        <ul>
          <li><strong>Chronological Age (Y/M/D)</strong> — school admissions, exam eligibility, passport applications.</li>
          <li><strong>Total Days</strong> — hospital length-of-stay, project duration, interest accrual.</li>
          <li><strong>Total Weeks</strong> — pregnancy tracking, employment probation, sprint planning.</li>
          <li><strong>Intervening Months</strong> — subscription billing counts, rental-agreement periods, EMI counts.</li>
        </ul>
        <p>
          Need to know your age today instead? Use the main{" "}
          <Link to="/age-calculator" className="text-primary underline">Age Calculator hub</Link>. Planning long-term? Jump
          to the <Link to="/age-calculator/retirement-pension" className="text-primary underline">Retirement & Pension Calculator</Link> to
          see how many years remain until state-pension eligibility.
        </p>
      </ContentSection>
      <FaqBlock faqs={FAQS} />
      <SubHubGrid excludePath="/age-calculator/chronological-date-difference" />
    </Layout>
  );
}